'use client';
import { useLayoutEffect, useRef, type ComponentProps, type RefObject } from 'react';
import { Textarea } from '../../components/ui/shadcn/textarea';
import { cn } from '../../lib/utils';

/** The composer and inline message editor share field geometry and touch typography. Both draw their own
 *  bordered box, so the field itself is the shared `Textarea` at its `line` variant and only the growth
 *  cap and the flex sizing are added here. */
export function ChatTextarea({ variant, className, ...props }: ComponentProps<'textarea'> & { variant: 'full' | 'compact' }) {
  return <Textarea variant="line" {...props} className={cn(
    // Cap growth by the live viewport too, so a landscape phone keeps its transcript readable.
    'max-h-[min(--spacing(40),22dvh)] min-w-0 flex-1 resize-none text-body md:text-ui pointer-coarse:text-body placeholder:text-muted-foreground',
    variant === 'full' ? 'px-2 py-2' : 'rounded-lg border border-border bg-background px-3 py-2',
    className,
  )} />;
}

/** Hold the form while measuring a shrinking field so transcript scroll offsets are not clamped. */
export function useChatTextareaSize(ref: RefObject<HTMLTextAreaElement | null>, input: string, locked = false) {
  const previousInput = useRef<string | null>(null);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el || locked) return;
    // Empty placeholders may wrap; only actual text determines the field's height.
    if (input === '') {
      el.style.removeProperty('height');
    } else {
      const reset = previousInput.current === null || !input.startsWith(previousInput.current);
      const form = el.form!;
      const formHeight = form.style.height;
      if (reset) {
        form.style.height = getComputedStyle(form).height;
        el.style.height = 'auto';
      }
      // scrollHeight excludes borders, but the compact field is border-box sized.
      const height = el.scrollHeight + el.offsetHeight - el.clientHeight;
      if (height !== el.offsetHeight && el.style.height !== `${height}px`) el.style.height = `${height}px`;
      if (reset) form.style.height = formHeight;
    }
    previousInput.current = input;
  }, [input, locked, ref]);
}
